import { getCurrentSession } from "./cognito";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

export interface CheckPropertyMediaRequestResponse {
	exists: boolean;
	canRequest: boolean;
}

export async function checkPropertyMediaRequest(
	realEstateId: string
): Promise<CheckPropertyMediaRequestResponse> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}
	const url = `${API_BASE_URL}/property-media-requests/check?real_estate_id=${encodeURIComponent(realEstateId)}`;
	const response = await fetch(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to check property media request: ${response.statusText}`
		);
	}
	return await response.json();
}

export interface PropertyMediaRequest {
	id: string;
	status: "Completed" | "Processing" | "Pending" | "Cancelled" | "Failed";
	requested_at: string;
	updated_at: string;
	notes?: string;
	real_estate: {
		parcel_data: {
			address: string;
		};
		property_id: string;
		address: string;
		county: string;
		state: string;
		zip: string;
	};
	medias?: Array<{
		id: number;
		url: string;
		real_estate_id: string;
		request_id: number;
		created_at: string;
	}>;
}

export async function getMyPropertyMediaRequests(): Promise<
	PropertyMediaRequest[]
> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(`${API_BASE_URL}/property-media-requests/me`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to get property media requests: ${response.statusText}`
		);
	}

	return await response.json();
}

export interface PropertyMediaRequestsResponse {
	data: PropertyMediaRequest[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	statusCounts: {
		Pending: number;
		Processing: number;
		Completed: number;
		Failed: number;
		Cancelled: number;
	};
}

export async function getAllPropertyMediaRequests(
	page: number = 1,
	pageSize: number = 12,
	search?: string,
	status?: string
): Promise<PropertyMediaRequestsResponse> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const params = new URLSearchParams({
		page: page.toString(),
		pageSize: pageSize.toString(),
		...(search && { search }),
		...(status && status !== "all" && { status }),
	});

	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/admin/all?${params}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to get property media requests: ${response.statusText}`
		);
	}

	return await response.json();
}

export async function getPropertyMediaRequestById(
	id: string
): Promise<PropertyMediaRequest> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/${id}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to get property media request: ${response.statusText}`
		);
	}

	return await response.json();
}

export interface PropertyMediaRefundListItem {
	id: string;
	status?: string;
	order_id?: string;
	amount_cents?: number;
	created_at?: string;
	updated_at?: string;
	stripe_refund_id?: string;
	property_media_request_id?: string | null;
	[key: string]: unknown;
}

export interface AdminRefundsListResponse {
	data: PropertyMediaRefundListItem[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export async function getAdminRefunds(params: {
	page?: number;
	pageSize?: number;
	order_id?: string;
	status?: string;
}): Promise<AdminRefundsListResponse> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const q = new URLSearchParams({
		page: String(params.page ?? 1),
		pageSize: String(params.pageSize ?? 20),
		...(params.order_id?.trim() && { order_id: params.order_id.trim() }),
		...(params.status?.trim() && { status: params.status.trim() }),
	});
	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/admin/refunds?${q}`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			(errorData as { message?: string }).message ||
				`Failed to list refunds: ${response.statusText}`
		);
	}
	return await response.json();
}

export async function getAdminRefundById(
	id: string
): Promise<PropertyMediaRefundListItem & Record<string, unknown>> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/admin/refunds/${encodeURIComponent(id)}`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			(errorData as { message?: string }).message ||
				`Failed to load refund: ${response.statusText}`
		);
	}
	return await response.json();
}

export async function initiatePropertyMediaRefund(body: {
	order_id: string;
	property_media_request_id?: string;
	amount_cents?: number;
}): Promise<unknown> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/refunds`,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		}
	);
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			(errorData as { message?: string }).message ||
				`Refund failed: ${response.statusText}`
		);
	}
	return await response.json();
}

/** One row from GET /property-media-requests/payments/me */
export interface PropertyMediaPaymentMeItem {
	order: {
		id: string;
		stripe_payment_status?: string;
		amount_total_cents?: number;
		currency?: string;
		created_at?: string;
		requests?: Array<unknown>;
		[key: string]: unknown;
	};
	payment: {
		id: string;
		order_id: string;
		amount_cents: number;
		currency: string;
		status: string;
		created_at: string;
		stripe_invoice_id?: string | null;
		[key: string]: unknown;
	};
	refunds: unknown[];
}

export interface PropertyMediaPaymentsMeResponse {
	data: PropertyMediaPaymentMeItem[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export async function getMyPropertyMediaPayments(params?: {
	page?: number;
	pageSize?: number;
}): Promise<PropertyMediaPaymentsMeResponse> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const page = params?.page ?? 1;
	const pageSize = params?.pageSize ?? 20;
	const q = new URLSearchParams({
		page: String(page),
		pageSize: String(pageSize),
	});
	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/payments/me?${q}`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			(errorData as { message?: string }).message ||
				`Failed to load payments: ${response.statusText}`
		);
	}
	const body = (await response.json()) as unknown;
	if (Array.isArray(body)) {
		const rows = body as PropertyMediaPaymentMeItem[];
		return {
			data: rows,
			total: rows.length,
			page: 1,
			pageSize: rows.length || 20,
			totalPages: 1,
		};
	}
	if (
		body &&
		typeof body === "object" &&
		Array.isArray((body as PropertyMediaPaymentsMeResponse).data)
	) {
		const b = body as PropertyMediaPaymentsMeResponse;
		return {
			data: b.data,
			total: Number(b.total) || b.data.length,
			page: Number(b.page) || page,
			pageSize: Number(b.pageSize) || pageSize,
			totalPages:
				Number(b.totalPages) ||
				Math.max(1, Math.ceil((Number(b.total) || 0) / pageSize)),
		};
	}
	return {
		data: [],
		total: 0,
		page,
		pageSize,
		totalPages: 1,
	};
}

export interface PaymentOrderDocumentsResponse {
	receipt_url?: string | null;
	invoice_pdf_url?: string | null;
	invoice_url?: string | null;
	invoice_id?: string | null;
}

export async function getPaymentDocumentsForOrder(
	orderId: string
): Promise<PaymentOrderDocumentsResponse> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const response = await fetch(
		`${API_BASE_URL}/property-media-requests/payments/me/orders/${encodeURIComponent(orderId)}/documents`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			(errorData as { message?: string }).message ||
				`Failed to load documents: ${response.statusText}`
		);
	}
	return (await response.json()) as PaymentOrderDocumentsResponse;
}
